import React, { useState, useEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UpdateIcon from '@mui/icons-material/Update';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';

import { UPLOAD_TAX_CERTIFICATE } from "../../Graphql/queries";
import CustomerHeader from "../Common/customerHeader";
import SideMenu from "../../components/organisms/sidebar";
import MobileHeader from "../Common/mobileHeader";
import { toggleSnackbar } from "../../ReactiveVariables/index";
import { useDropzone } from 'react-dropzone';
import { useReactiveVar } from "@apollo/client";
import { userDeatils } from "../../ReactiveVariables/index";
import {
  MAX_TAX_CERT_BYTES,
  TAX_CERT_ACCEPT,
  readTaxCertFile,
  rejectedFileMessage,
  submitTaxCertificate,
  validateTaxCertFile,
} from "./taxCertificate.utils";

const dropzoneStyle = {
  border: '2px dashed #cccccc',
  borderRadius: '4px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
};

export default function TaxExemption(props) {
  const user = useReactiveVar(userDeatils);
  const userDetails = user?.data?.currentUserDetails || user?.data?.login || user?.data?.createUserIdentity;
  const customerId = userDetails?._id;
  const [file, setFile] = useState(null);
  const [fileRaw, setFileRaw] = useState(null);
  const [fileUploaded, setFileUploaded] = useState(false);
  const taxStatus = userDetails?.taxExempt;
  const pendingApproval = userDetails?.taxCert && !taxStatus;
  const profileTaxCert = userDetails?.taxCert;
  const isTaxExempt = taxStatus === true;
  const [taxCertURL, setTaxCertURL] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Refs, not state, so a second drop or click in the same render sees them.
  const latestSelection = useRef(0);
  const uploadInFlight = useRef(false);

  const [uploadPdf] = useLazyQuery(UPLOAD_TAX_CERTIFICATE, {
    fetchPolicy: "network-only",
  });

  const showError = (message) =>
    toggleSnackbar({ status: true, message, variant: "error" });

  // MAIN-883: only a PDF, PNG or JPEG under 10 MB is read, and only one whose
  // bytes really are that type, matching what the API accepts.
  const onDrop = async (acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;
    latestSelection.current += 1;
    const selection = latestSelection.current;
    const checked = validateTaxCertFile(selectedFile);
    const read = checked.ok ? await readTaxCertFile(selectedFile) : checked;
    // A slower read of an earlier drop must not replace a newer choice.
    if (selection !== latestSelection.current) return;
    if (!read.ok) {
      setFile(null);
      setFileRaw(null);
      showError(read.message);
      return;
    }
    setFileRaw(selectedFile);
    setFile(read.base64);
  };

  const onDropRejected = (rejections) => showError(rejectedFileMessage(rejections[0]));

  // Success is shown only once the API has returned the stored certificate.
  const upload = async () => {
    if (uploadInFlight.current) return;
    uploadInFlight.current = true;
    setUploading(true);
    const outcome = await submitTaxCertificate(uploadPdf, {
      file,
      customerId,
      filename: fileRaw.name,
      mimetype: fileRaw.type,
    });
    uploadInFlight.current = false;
    setUploading(false);
    if (!outcome.ok) {
      setFileUploaded(false);
      showError(outcome.message);
      return;
    }
    setTaxCertURL(outcome.url);
    setFileUploaded(true);
    toggleSnackbar({
      status: true,
      message: "Certificate successfully uploaded.",
      variant: "success",
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: TAX_CERT_ACCEPT,
    maxSize: MAX_TAX_CERT_BYTES,
    multiple: false,
  });

  useEffect(() => {
    if (userDetails?.taxCert) {
      setTaxCertURL(userDetails?.taxCert);
    }
  }, [userDetails]);

  return (
    <div>
      <CustomerHeader title="Tax Exemption" />
      <MobileHeader selected="Tax Exemption" />
      <SideMenu selected="Taxes" user="customer" />
      <div className="contentWrap contentWrap-customer-profile">
        <div className="customer-profile-wrap">
          <div className="customer-profile mt-0 mb-0">
            <div className="customer-profile-title">
              <h4 className="h4">
                <img
                  src="../assets/images/icon-contact-info.svg"
                  className="img-fluid"
                  alt="Contact Information Icon"
                />
                Tax Exemption
              </h4>
            </div>
            { isTaxExempt ?
              <div className="row customer-profile-details">
                <div className="col text-center pt-4">
                  <div className="pb-4"><CheckBoxIcon style={{ fontSize: '64px', color: "green" }}/></div>
                  <p>Your tax exemption certificate has been approved. All orders placed are now being processed without the addition of any taxes.</p>
                </div>
              </div>
             : (taxStatus === false && profileTaxCert) ?
                <div className="row customer-profile-details">
                  <div className="col text-center pt-4">
                    <div className="pb-4"><DoNotDisturbIcon style={{ fontSize: '64px', color: "red" }} /></div>
                    <p>Your tax exemption certificate was not approved, please contact us for more details.</p>
                  </div>
                </div>
             : <div className="row customer-profile-details">
              { ((fileUploaded || pendingApproval || taxCertURL)) ?
                <div className="col text-center">
                  <div className="py-4"><UpdateIcon fontSize="large"/></div>
                  <p>Your certificate has been successfully submitted to our team and is under review. Once a decision has been made this page will display your tax exemption status. You can view your uploaded certificate <a target="_blank" rel="noreferrer" href={taxCertURL}>here</a>.</p>
                  <p>Please feel free to reach out to us with any questions or concerns.</p>
                </div>
                : <div className="col">
                    <p>If you qualify for tax exemption, please upload your tax exemption certificate. Once we receive it, our team will promptly review your submission, and upon approval, we will apply the tax exemption to your profile.</p>

                    <div className="d-flex justify-content-center pt-4">
                      <div {...getRootProps()} style={dropzoneStyle}>
                        <input {...getInputProps()} />
                        {file ? (
                          <div>
                            <div>Selected file: {fileRaw.name}</div>
                          </div>
                        ) : (
                          <div>Drag and drop a PDF, PNG or JPEG file (up to 10 MB) here, <br/> or click to select one.</div>
                        )}
                      </div>
                    </div>
                    { file && (
                      <div className="d-flex justify-content-center pt-4">
                        <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} disabled={uploading} onClick={upload}>
                          {uploading ? "Uploading..." : "Upload file"}
                        </Button>
                      </div>
                    ) }
                  </div>
              }
            </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}
